import React, { useState } from 'react';

import { DataStatus } from '../types/dataStatus';

/**
 * OSM-1983 — Shared empty-state shown when an account's data cannot be
 * retrieved from BigQuery (e.g. missing/incomplete dataset from a failed
 * onboarding, or a BQ query error). Renders instead of the module page's
 * main content so the user is never faced with a blank screen. The
 * sidebar stays mounted separately, so the user can pick a different
 * account.
 */
interface CustomerDataErrorProps {
  /**
   * Optional callback to retry the page's data fetch. When provided, a
   * RETRY button is rendered alongside the primary action. May return
   * void or a Promise; the button shows a loading state until the
   * returned promise resolves.
   *
   * OSM-1983: only pass this when the status is actually retryable. Offering
   * Retry for an account that was never onboarded just wastes the user's time.
   */
  onRetry?: () => void | Promise<unknown>;
  /**
   * Optional override for the body copy — defaults to copy chosen from
   * `status`.
   */
  message?: string;
  /**
   * OSM-1983 — why the data is missing, so the copy can tell the user whether
   * this is permanent ("account not present") or transient ("try again").
   * Falls back to the transient wording when unknown, which is what this
   * component said unconditionally before.
   */
  status?: DataStatus;
}

const NOT_ONBOARDED_HEADING = 'Account not present';
const NOT_ONBOARDED_MESSAGE =
  "This account isn't set up yet, so there's no data to show. " +
  'Please select a different account from the sidebar, or contact ' +
  'support if you think this is a mistake.';

const UNAVAILABLE_HEADING = 'Problem retrieving account data';
const UNAVAILABLE_MESSAGE =
  "This account's data is temporarily unavailable. " +
  'Please select a different account from the sidebar, or contact ' +
  'support if the issue persists.';

const NO_DATA_HEADING = 'No data to show';
const NO_DATA_MESSAGE =
  "This account doesn't have any data for the current selection yet. " +
  'Try a different date range, or select another account from the sidebar.';

const copyFor = (status?: DataStatus) => {
  switch (status) {
    case 'not_onboarded':
      return { heading: NOT_ONBOARDED_HEADING, message: NOT_ONBOARDED_MESSAGE };
    case 'no_data':
      return { heading: NO_DATA_HEADING, message: NO_DATA_MESSAGE };
    default:
      return { heading: UNAVAILABLE_HEADING, message: UNAVAILABLE_MESSAGE };
  }
};

const CustomerDataError: React.FC<CustomerDataErrorProps> = ({
  onRetry,
  message,
  status,
}) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const copy = copyFor(status);
  const bodyCopy = message ?? copy.message;

  const handleRetry = async () => {
    if (!onRetry || isRetrying) return;
    setIsRetrying(true);
    try {
      await onRetry();
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <div
      className="flex flex-col items-center justify-center min-h-[50vh] px-6 text-center"
      data-testid="customer-data-error"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 48 48"
        aria-hidden="true"
        className="w-16 h-16 mb-6"
      >
        <path
          d="M24 4 L44 40 H4 Z"
          fill="#FEF3C7"
          stroke="#F59E0B"
          strokeWidth="3"
          strokeLinejoin="round"
        />
        <rect x="22" y="18" width="4" height="12" rx="1" fill="#B45309" />
        <circle cx="24" cy="34" r="2" fill="#B45309" />
      </svg>
      <h2 className="text-xl font-bold text-primarygray mb-2">{copy.heading}</h2>
      <p className="text-base text-secondarygray max-w-md mb-6">{bodyCopy}</p>
      {onRetry && (
        <button
          type="button"
          onClick={handleRetry}
          disabled={isRetrying}
          aria-busy={isRetrying}
          className="inline-flex items-center gap-2 text-sm font-bold py-2 px-6 rounded-full border border-thirdgray text-thirdgray hover:bg-gray50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="customer-data-error-retry"
        >
          {isRetrying && (
            <span
              className="inline-block w-4 h-4 border-2 border-thirdgray border-t-transparent rounded-full animate-spin"
              aria-hidden="true"
            />
          )}
          {isRetrying ? 'RETRYING…' : 'RETRY'}
        </button>
      )}
    </div>
  );
};

export default CustomerDataError;
